'use client';

import { useCommendaOSRead } from '@commenda/api/commenda-os-dsl';
import { AddressInput, Input, LoadingIndicator } from '@commenda/kakashi';
import { IAddress } from '@commenda/types';
import { Country } from '@prisma/client';
import { useContext, useEffect, useMemo } from 'react';
import { DSLContextMarker } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import {
  ADDRESS_TEST_VALUE,
  REGISTERED_AGENT_TEST_NAME,
} from '../../preview/test-value-registry';
import { isRuleActive } from '../../utils/validation';
import type { DataSourceConfig } from '@commenda/types';
import { STANDARD_CONTEXT_KEYS } from '../../types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

interface IPartialAddress {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface IRegisteredAgentFormData {
  name?: string;
  address?: IPartialAddress;
}

interface RegisteredAgentFieldProps {
  fieldId: string;
  label?: string;
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  dataSource?: DataSourceConfig;
  props?: {
    /** Operation mode: 'read' for display only, 'read-write' for editable */
    mode?: 'read' | 'read-write';
    /** Default country for address input */
    defaultCountry?: string;
    disabled?: boolean;
  };
}

/**
 * RegisteredAgent Component
 *
 * A specialized component for collecting the registered agent (name + address)
 * for a US corporation from Commenda OS. This component:
 *
 * 1. Reads the current registered agent from corporation.generalCorporationInfo.registeredAgent
 * 2. Renders a name input and an address input inline
 * 3. Supports read-only or read-write modes
 * 4. On submit, writes via updateBusinessEntity using countrySpecificFields.US.*
 *
 * Configuration:
 * - mode: 'read' for display only, 'read-write' for editable
 * - defaultCountry: default country for address input (defaults to 'US')
 */
export const RegisteredAgentFieldComponent = ({
  fieldId,
  label: labelOverride,
  guidance,
  description: descriptionOverride,
  validation,
  dataSource,
  props = {},
}: RegisteredAgentFieldProps) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
    isPreview,
    registerPreviewFillHandler,
  } = useContext(FormContext);

  const {
    mode = 'read-write',
    defaultCountry = 'US',
    disabled: propsDisabled = false,
  } = props;

  const contextBindings = dataSource?.commendaOS?.contextBindings;
  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;
  const VALID_CONTEXT_KEYS: readonly string[] = STANDARD_CONTEXT_KEYS;

  // Build effective context by merging global context with bound field values
  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };

    if (contextBindings) {
      for (const binding of contextBindings) {
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) continue;
        if (!VALID_CONTEXT_KEYS.includes(binding.contextKey)) continue;

        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }

    const filteredCtx: typeof ctx = {};
    for (const key of Object.keys(ctx)) {
      if (VALID_CONTEXT_KEYS.includes(key)) {
        filteredCtx[key] = ctx[key];
      }
    }

    return filteredCtx;
  }, [dslContext, formData, contextBindings]);

  const hasMissingBoundContext = useMemo(() => {
    if (!contextBindings || contextBindings.length === 0) return false;

    const validBindings = contextBindings.filter((binding) =>
      VALID_CONTEXT_KEYS.includes(binding.contextKey)
    );
    if (validBindings.length === 0) return false;

    return validBindings.some((binding) => {
      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        return (
          dslContext?.[binding.contextKey] === undefined ||
          dslContext?.[binding.contextKey] === null
        );
      }
      const fieldValue = formData[binding.sourceFieldId];
      return (
        fieldValue === undefined || fieldValue === null || fieldValue === ''
      );
    });
  }, [contextBindings, formData, dslContext]);

  // Fetch existing registered agent data from commendaOS
  // Path must start with 'corporation.' so the DSL evaluator knows which entity to query
  const readPath = useMemo(() => {
    if (!effectiveContext?.corporationId) return null;
    return 'corporation.generalCorporationInfo.registeredAgent';
  }, [effectiveContext?.corporationId]);

  const { data: currentAgent, isLoading: valueLoading } = useCommendaOSRead<{
    name?: string;
    address?: IAddress;
  }>(readPath, effectiveContext as { companyId: number } | undefined, {
    skip: !readPath || hasMissingBoundContext,
  });

  // Derive displayed data from form state (user edits) merged with server data
  const agentData = useMemo((): IRegisteredAgentFormData => {
    const formValue = formData[fieldId] as IRegisteredAgentFormData | undefined;

    if (!formValue || Object.keys(formValue).length === 0) {
      return {
        name: currentAgent?.name || '',
        address: currentAgent?.address || {},
      };
    }

    return {
      name: formValue.name ?? (currentAgent?.name || ''),
      address:
        formValue.address && Object.keys(formValue.address).length > 0
          ? formValue.address
          : currentAgent?.address || {},
    };
  }, [formData, fieldId, currentAgent]);

  // Sync fetched data to form state on load, and always ensure a default country is set
  useEffect(() => {
    const existingData = formData[fieldId] as
      | IRegisteredAgentFormData
      | undefined;
    const hasExistingCountry = !!existingData?.address?.country;

    if (!currentAgent) {
      if (!hasExistingCountry && !valueLoading) {
        updateField(fieldId, {
          name: existingData?.name || '',
          address: {
            ...(existingData?.address || {}),
            country: defaultCountry,
          },
        });
      }
      return;
    }

    const agentAddress = {
      ...(currentAgent.address || {}),
      country: currentAgent.address?.country || defaultCountry,
    };

    if (!existingData || Object.keys(existingData).length === 0) {
      updateField(fieldId, {
        name: currentAgent.name || '',
        address: agentAddress,
      });
      return;
    }

    const needsUpdate =
      (!existingData.name && currentAgent.name) ||
      ((!existingData.address ||
        Object.keys(existingData.address).length === 0) &&
        currentAgent.address) ||
      !hasExistingCountry;

    if (needsUpdate) {
      updateField(fieldId, {
        name: existingData.name || currentAgent.name || '',
        address:
          existingData.address && Object.keys(existingData.address).length > 0
            ? {
                ...existingData.address,
                country: existingData.address.country || agentAddress.country,
              }
            : agentAddress,
      });
    }
  }, [
    currentAgent,
    fieldId,
    formData,
    updateField,
    defaultCountry,
    valueLoading,
  ]);

  const effectiveLabel = labelOverride || 'Registered Agent';
  const effectiveDescription = descriptionOverride;

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);
  const isReadOnly = mode === 'read';
  const isDisabled =
    propsDisabled ||
    isFieldDisabled(fieldId) ||
    isReadOnly ||
    hasMissingBoundContext;

  // Preview self-fill: this component owns the agent value shape. When the
  // corporation has a real registered agent, the OS sync above already filled
  // it; otherwise synthesize a test agent (in-memory only).
  useEffect(() => {
    if (!isPreview || !registerPreviewFillHandler || !fieldId) return undefined;
    registerPreviewFillHandler(fieldId, () => {
      const existingData = formData[fieldId] as
        | IRegisteredAgentFormData
        | undefined;
      if (existingData?.name) return { kind: 'already-filled' };
      if (valueLoading) return { kind: 'loading' };
      updateField(fieldId, {
        name: REGISTERED_AGENT_TEST_NAME,
        address: {
          ...(existingData?.address ?? {}),
          ...ADDRESS_TEST_VALUE,
          country: existingData?.address?.country || ADDRESS_TEST_VALUE.country,
        },
      });
      return { kind: 'filled' };
    });
    return () => registerPreviewFillHandler(fieldId, null);
  }, [
    isPreview,
    registerPreviewFillHandler,
    fieldId,
    formData,
    valueLoading,
    updateField,
  ]);

  const handleNameChange = (name: string) => {
    const newData = { ...agentData, name };
    updateField(fieldId, newData);
    validateField(fieldId, newData, validation?.rules);
  };

  const handleAddressChange = (addr: IAddress) => {
    const newData = { ...agentData, address: addr };
    updateField(fieldId, newData);
    validateField(fieldId, newData, validation?.rules);
  };

  if (valueLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  if (hasMissingBoundContext) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <AddressInput
          address={{
            addressLine1: '',
            postalCode: '',
            city: '',
            state: '',
            country: '',
          }}
          onChange={() => {}}
          defaultCountry={defaultCountry as Country}
          disabled
        />
      </div>
    );
  }

  if (!effectiveContext?.corporationId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Corporation context required to load registered agent.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(effectiveLabel || effectiveDescription) && (
        <LabelWithGuidanceTooltip
          label={effectiveLabel}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={effectiveDescription}
        />
      )}

      {/* Registered Agent Name */}
      <div className="mb-3">
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Name {isRequired && <span className="text-text-error">*</span>}
        </label>
        <Input
          value={agentData.name || ''}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Registered Agents Inc."
          disabled={isDisabled}
        />
      </div>

      {/* Registered Agent Address */}
      <div className="mb-1">
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Address
        </label>
        <AddressInput
          address={
            (agentData.address || {
              addressLine1: '',
              postalCode: '',
              city: '',
              state: '',
              country: '',
            }) as IAddress
          }
          onChange={handleAddressChange}
          defaultCountry={defaultCountry as Country}
          disabled={isDisabled}
        />
      </div>

      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
